import React, { createContext, useContext, useReducer } from 'react';

import { CreditAgreement } from '@finley-technologies/finley-types';

type CreditAgreementContextValue = {
  orgSlugs: string[];
  setOrgSlugs: (orgSlugs: string[]) => void;
  parties: CreditAgreement.DomainEntity.PartyEntities;
};

const CreditAgreementContext = createContext<null | CreditAgreementContextValue>(
  null,
);

/**
 * Credit agreement context hook
 * @value isIncrementVersion boolean
 *   - if true, and wip version is not strictly increasing, then auto increments the latest committed version
 *   - otherwise, the wip version will be used when the wip is committed
 */
export const useCreditAgreementContext = () => {
  const context = useContext(CreditAgreementContext);

  if (!context) {
    throw new Error(
      'CreditAgreementContextProvider must be present to use CreditAgreementContext',
    );
  }

  return context;
};

type ContextState = {
  orgSlugs: string[];
};
const initState = ({ orgSlugs }: { orgSlugs: string[] }) => {
  const initialState: ContextState = { orgSlugs };
  return initialState;
};

const creditAgreementReducer = (
  state: ContextState,
  action: {
    type: string;
    payload: any;
  },
) => {
  switch (action.type) {
    case 'SET_ORG_SLUGS':
      return {
        ...state,
        orgSlugs: action.payload,
      };
    default:
      return state;
  }
};

type CreditAgreementContextProviderProps = {
  children: React.ReactNode;
  initOrgSlugs: string[];
  parties: CreditAgreement.DomainEntity.PartyEntities;
};
export const CreditAgreementContextProvider = ({
  children,
  initOrgSlugs,
  parties,
}: CreditAgreementContextProviderProps) => {
  const initialState = initState({ orgSlugs: initOrgSlugs });
  const [state, dispatch] = useReducer(creditAgreementReducer, initialState);

  const setOrgSlugs = (orgSlugs: string[]) => {
    dispatch({
      type: 'SET_ORG_SLUGS',
      payload: orgSlugs,
    });
  };

  return (
    <CreditAgreementContext.Provider
      value={{
        ...state,
        setOrgSlugs,
        parties,
      }}
    >
      {children}
    </CreditAgreementContext.Provider>
  );
};
